import { createEffect, createEvent, createStore, sample } from 'effector/compat'
import * as customerApi from '@api/customer/customer'
import * as validations from '@helpers/validations'
import { Customer, CustomerProfile } from '@api/customer/models'
// eslint-disable-next-line no-unused-vars
import { createForm, ErrorsInline, FormValidateParams, Message, SubmitParams } from 'src/infastruct/effector-react-form'
import dayjs from 'dayjs'
import { subscribeLoggerEffect } from 'src/infastruct/effector-utils/subscribe-sentry'
import { destroyCookie } from 'nookies'
import { useUnit } from 'effector-react/compat'
import { resetIsAuthorization } from '@entities/authorization/model'
import { clearHistorySuggestion } from 'src/features/suggestions/history'

export interface ErrorResponse {
    InvalidParameters: string[]
}

const isError = (err: unknown): err is Error => err instanceof Error
const isErrorsInline = (target: unknown): target is Record<string, string | null | undefined> => !isError(target)

//Глобальное получение профиля
const getCustomerProfileFx = createEffect<void, CustomerProfile, Error>(async () => {
    return await customerApi.getCustomerProfile()
})
subscribeLoggerEffect(getCustomerProfileFx)

export const getCustomerProfile = createEvent()
const customerProfileReset = createEvent()

export const customerLogout = createEvent()

export const $isLogging = createStore<boolean>(false).reset(customerProfileReset)

sample({
    clock: getCustomerProfileFx.doneData,
    fn: () => true,
    source: $isLogging
})

export const logoutFx = createEffect<void, void, Error>(async () => {
    await customerApi.logout()
    destroyCookie({}, 'customer_token', { path: '/' })
    destroyCookie({}, 'zdr_auth_token', { path: '/' })
    resetIsAuthorization()
    clearHistorySuggestion()
})
subscribeLoggerEffect(logoutFx)

sample({
    clock: customerLogout,
    target: logoutFx
})
sample({ clock: logoutFx.done, target: customerProfileReset })
/**
 * response error handle
 * @param values
 */
const responseError = (values: Customer | ErrorResponse | { isError: boolean }) => {
    const errors: ErrorsInline = {}
    if ('InvalidParameters' in values && values?.InvalidParameters?.length > 0) {
        values.InvalidParameters.forEach((x) => (errors[x] = 'Ошибка'))
    }
    return errors
}

/**
 * change customer event
 */
const changeCustomer = createEvent<SubmitParams<Customer>>()

/**
 * Change customer
 */
const changeCustomerFx = createEffect<SubmitParams<Customer>, void, unknown>({
    handler: async ({ values }) => {
        const response = await customerApi.postCustomerProfile(values)

        if (response) {
            const serverSideError = responseError(response)
            if (Object.keys(serverSideError).length > 0) {
                throw serverSideError
            }
        }
    }
})

subscribeLoggerEffect(changeCustomerFx)

sample({
    clock: changeCustomer,
    target: changeCustomerFx
})

sample({
    clock: changeCustomerFx.doneData,
    target: getCustomerProfile
})

const customerProfileInitialState: Record<number, CustomerProfile> = {}

const $customerProfile = createStore<CustomerProfile>(customerProfileInitialState as CustomerProfile)
    .on(getCustomerProfileFx.doneData, (state: CustomerProfile, { profile, genderTypes }) => {
        return {
            ...state,

            profile: {
                ...state.profile,
                ...profile
            },
            genderTypes: genderTypes
        }
    })
    .reset(customerProfileReset)

export const $customerProfileLoading = getCustomerProfileFx.pending

//Форма заполнения
const formValidate = ({ values }: FormValidateParams<Customer, { mode: 'change' }>) => {
    const errors: ErrorsInline = {}

    if (values.email && values.email.length > 0 && !validations.isEmail(values.email)) {
        errors['email'] = 'Введен некорректный email'
    }

    if (values.birthday) {
        const birthday = dayjs(values.birthday)
        const isValidDate = birthday.isValid()
        if (!isValidDate) {
            errors['birthday'] = 'Некорректная дата рождения'
        }
    }

    return errors
}

export const customerForm = createForm<Customer>({
    onSubmit: changeCustomer,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    validate: formValidate
})

/**
 * Handle response errors
 */
sample({
    clock: changeCustomerFx.failData,
    filter: isErrorsInline,
    fn: (clock: { [x: string]: Message }) => {
        const err: ErrorsInline = {}
        Object.keys(clock).forEach((key) => {
            err[key] = clock[key]
        })
        return err
    },
    target: customerForm.$outerErrorsInline
})
customerForm.$outerErrorsInline
export const useCustomerProfile = () => {
    return useUnit($customerProfile)
}

sample({
    clock: getCustomerProfile,
    target: getCustomerProfileFx
})
sample({ clock: customerForm.onSubmit, target: getCustomerProfile })

//export const useGetCustomerProfile = () => useEvent(getCustomerProfile)

export const stores = {
    $customerProfile
}
export const events = {
    customerProfileReset,
    customerProfileUpdateEvent: getCustomerProfile
}

export const effects = {
    getCustomerProfileFx
}

export const selectors = {
    useCustomerProfile
}
